// planCapabilities.ts — single source of truth for "what does this plan get."
//
// Replaces the binary `isPro` flag that used to be computed ad hoc wherever
// a component needed it, treating every paid tier (scout/pro/team/admin) as
// Pro-equivalent for everything (#677). Scout ($45), Pro ($75), and Team
// ($149) are meant to be differentiated — see Pricing.tsx's TIERS array for
// the user-facing feature list per tier, which is the definitive manifest
// of what's actually sold. This module is the code-side mirror of that
// manifest: the plan → capabilities mapping every gate should read from,
// instead of re-deriving its own `userPlan === "..."` comparison.
//
// ─── #677 audit findings (2026-07-29) ───────────────────────────────────
// Before adding this module, every existing `isPro` consumer across the
// frontend was audited against Pricing.tsx's copy AND the backend
// enforcement it actually talks to. Result: most of the Scout-vs-Pro
// differentiation this issue worried about was ALREADY correctly built —
// just scattered as separate inline ternaries in App.tsx with no single
// named model:
//
//   - Watchlist cap     → watchlistLimit below. Backend mirror: watchlistCap()
//                         in backend/internal/handlers/watchlist_handler.go
//                         (free=3, scout=10, pro/team/admin=unlimited).
//   - County geography  → isUnrestrictedPlan below (App.tsx's isAllowedCounty
//                         also folds in chosenCounties, so it stays a
//                         separate callback there, not a pure function of
//                         plan alone). Backend mirror: countyCapForPlan in
//                         profile_counties_handler.go — already correct,
//                         NOT touched by #677 (explicitly out of scope).
//   - Seller Leads      → canUseLeads + leadsTier below. Backend mirror:
//                         leadsAccessTierFor in leads_handler.go.
//   - Bid history/intel → deliberately NOT modeled here. GetPropertyBids
//                         (bid_handler.go) and GetBidIntelligence
//                         (bid_intelligence.go) give Scout the same
//                         real-time data as Pro inside its chosen counties
//                         (the old Scout delay was retired 2026-10-14) and
//                         the count-only / proRequired stub outside them,
//                         decided server-side. The frontend's
//                         `isPro` only chooses "render the real panel vs.
//                         the free teaser" — Scout must see the real panel
//                         so it renders the backend-supplied (correctly
//                         delayed/gated) data. Do not exclude scout from
//                         `isPro` for BidTimeline/BidIntelligencePanel; that
//                         would regress already-shipped, tested behavior.
//   - Email alert quota → deliberately NOT modeled here. alertQuotaApplies()
//                         in internal/alerts/engine_listings.go already
//                         exempts scout from the free tier's 5/month cap,
//                         same as pro/team (see AlertSettings.test.tsx
//                         "hides the quota notice for a Pro (or Scout/Team)
//                         user"). AlertSettings' `isPro` prop only toggles a
//                         cosmetic "you're on the free plan" banner, not
//                         access to alerts themselves — free users already
//                         get every alert type, just capped in volume on 2
//                         of them.
//
// What's genuinely still open, found during the audit and deliberately
// left AS-IS (see the #677 PR description for the full writeup — not
// fixed here because each is either out of this issue's scope or
// ambiguous enough that guessing would be worse than leaving it alone):
//   - Deal Analyzer (all 4 per-class variants): RESOLVED by #188. The lock is
//     back, with explicit gating (ProGate + redacted placeholders + upgrade
//     CTA) instead of blur+overlay: free users get
//     FREE_ANALYZER_USES_PER_SESSION distinct properties per session
//     (lib/dealAnalyzerGate.ts); scout/pro/team/admin are never gated, which
//     matches Pricing.tsx (analyzer listed on Scout and Pro).
//   - PropertyDetail's tokenized share-link (vs. the plain in-app deep
//     link) isn't mentioned anywhere in Pricing.tsx's copy for any tier.
//     Genuinely ambiguous whether Scout should get it — left isPro-gated
//     (Scout included) rather than guessing either direction.
//
// Explicitly NOT modeled (out of scope for #677 — see #678): ML analysis
// quota (no ML feature exists yet), Team seat count (no multi-user/invite
// system exists), Team API access (no API-key system exists). Add fields
// here once those features actually exist; don't stub them out ahead of
// time.

export type UserPlan = "free" | "scout" | "pro" | "team" | "admin";

// Mirrors backend/internal/handlers/watchlist_handler.go's watchlistCap().
// PRO_WATCH_LIMIT is a large finite number (not Infinity) so it stays a
// plain, JSON-safe number for straightforward `watchLimit >= watchLimitMax`
// comparisons elsewhere — no real user will ever watch 10,000 properties.
export const FREE_WATCH_LIMIT = 3;
export const SCOUT_WATCH_LIMIT = 10;

// Saved searches are buy boxes (the "Saved" row in Leads). Mirrors BuyBoxCap in
// backend/internal/entitlements: Free has none (the Leads gate blocks it
// first), Scout 15, pro/team/admin unlimited (the same 10000 sentinel the
// watchlist uses).
export const FREE_BUY_BOX_LIMIT = 0;
export const SCOUT_BUY_BOX_LIMIT = 15;
export const PRO_BUY_BOX_LIMIT = 10000;
export const PRO_WATCH_LIMIT = 10000;

// Mirrors backend/internal/entitlements' FreeCountyCap / ScoutCountyCap /
// NoCountySelection. UNRESTRICTED_COUNTY_LIMIT (-1) means the plan doesn't
// participate in county selection at all (pro/team/admin see everything), NOT
// "zero counties" — check for < 0 before treating it as a number.
export const FREE_COUNTY_LIMIT = 1;
export const SCOUT_COUNTY_LIMIT = 3;
export const UNRESTRICTED_COUNTY_LIMIT = -1;

export interface PlanCapabilities {
  /**
   * Any paid tier (scout/pro/team/admin). Kept deliberately coarse — it
   * should only gate features confirmed (via the #677 audit above) to have
   * NO per-tier variation today, e.g. Portfolio Analytics in App.tsx.
   * Don't reach for this to gate a NEW feature without checking
   * Pricing.tsx's TIERS array first — prefer a specific field below (or
   * add one) if the tiers actually differ on it.
   */
  isPro: boolean;
  /**
   * Pro/team/admin only — excludes scout. Drives the geography gate
   * (isAllowedCounty in App.tsx mirrors backend fullAccessAllowed): Scout
   * gets full data only inside its chosen counties, not everywhere.
   */
  isUnrestrictedPlan: boolean;
  /** Seller Leads access. Every paid tier gets full rows (owner, mailing,
   *  equity). Mirrors entitlements.LeadsTier in the backend. */
  canUseLeads: boolean;
  /**
   * How LeadsView scopes the leads table. Both values get FULL rows.
   *
   * "scout" = county-scoped: the server confines /leads and /notice-leads to
   * the user's chosen counties whatever county params the client sends, the
   * saved-search cap is SCOUT_BUY_BOX_LIMIT, and the statewide buyer-list
   * export is hidden (it is Pro-only server-side). "full" = pro/team/admin,
   * statewide.
   *
   * Until 2026-10-14 "scout" also meant a teaser: owner/mailing/equity were
   * blanked until the user spent one of 50 monthly unlock credits. That is
   * gone; Scout is Pro-level data inside its chosen counties.
   */
  leadsTier: "scout" | "full";
  /** Max watchlist size for this plan. Mirrors watchlistCap() in
   *  watchlist_handler.go — keep both in sync if either changes. */
  watchlistLimit: number;
  /** Max saved searches (buy boxes). Mirrors BuyBoxCap in
   *  backend/internal/entitlements; 10000 means unlimited. */
  buyBoxLimit: number;
  /**
   * Max counties this plan may choose, or UNRESTRICTED_COUNTY_LIMIT (-1)
   * for plans that don't pick counties at all. Mirrors CountyCap in
   * backend/internal/entitlements.
   *
   * Added so CountyPickerModal stops deriving this itself — it carried a
   * private `plan === "free" ? 1 : 3`, which is the exact shape of drift
   * this module exists to prevent (and which would have silently given a
   * future 4th tier Scout's cap).
   */
  countyLimit: number;
  /**
   * Whether there is a higher plan to sell this user: free and scout yes,
   * pro/team/admin no. Gates the UpgradePrompt. It used to be gated on
   * `!isPro`, and isPro includes scout — so a Scout user hitting their
   * watchlist cap, a locked card or an out-of-area field got no prompt at all,
   * even though Pro is exactly what they would upgrade to.
   */
  hasHigherPlan: boolean;
}

/**
 * getPlanCapabilities — the plan→capabilities map #677 asked for. Pure
 * function of the plan string; gates that need additional runtime state
 * (e.g. isAllowedCounty, which also needs the user's chosenCounties) stay
 * in App.tsx as their own derived callback rather than living here.
 */
export function getPlanCapabilities(plan: UserPlan): PlanCapabilities {
  const isPro = plan === "scout" || plan === "pro" || plan === "team" || plan === "admin";
  const isUnrestrictedPlan = plan === "pro" || plan === "team" || plan === "admin";
  const leadsTier: "scout" | "full" = plan === "scout" ? "scout" : "full";
  const watchlistLimit =
    plan === "scout" ? SCOUT_WATCH_LIMIT : isPro ? PRO_WATCH_LIMIT : FREE_WATCH_LIMIT;
  const buyBoxLimit =
    plan === "scout" ? SCOUT_BUY_BOX_LIMIT : isPro ? PRO_BUY_BOX_LIMIT : FREE_BUY_BOX_LIMIT;
  const countyLimit = isUnrestrictedPlan
    ? UNRESTRICTED_COUNTY_LIMIT
    : plan === "scout"
      ? SCOUT_COUNTY_LIMIT
      : FREE_COUNTY_LIMIT;

  return {
    isPro,
    isUnrestrictedPlan,
    canUseLeads: isPro,
    leadsTier,
    watchlistLimit,
    buyBoxLimit,
    countyLimit,
    hasHigherPlan: !isUnrestrictedPlan,
  };
}
